import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  AccountManager,
  ApiManager,
  Bus,
  isUserNeedBankAccountNumbers,
  isUserNeedKyc,
  isUserUnderVerification,
  Logger,
  NotificationManager,
  useApplicationState,
  useSettings,
} from '@atlas/core';

import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';
import { getCurrentCulture } from 'src/components/Sportsbook/altenar/model/getCurrentCulture';

import { buildPathFromRoute, parsePathToPageProps } from './model/altenarRoutes';
import { loadAltenarScript } from './model/loadAltenarScript';
import { signIn } from './model/signIn';
import { useAltenarRoutes } from './model/useAltenarRoutes';
import { useUserSettings } from './model/useUserSettings';

const CONTAINER_ID = 'template-altenar-container';

const ensureAltenarLoaded = async () => {
  if (typeof window === 'undefined' || window.altenarWSDK) return;
  await loadAltenarScript();
};

const fetchAltenarToken = async () => {
  const authToken = AccountManager.getToken() || ApiManager.getAuthorizationToken();
  const url = new URL('betting/client/aa/client-token/create', ApiManager.getHostname('core'));

  //using fetch cause response type text, not json
  const response = await fetch(url.href, {
    headers: {
      ...ApiManager.getDefaultHeaders(),
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    method: 'GET',
    mode: 'cors',
  });
  if (!response.ok) throw new Error(`Altenar token request failed: ${response.status}`);
  const rawText = (await response.text()).trim();
  if (rawText.startsWith('"') && rawText.endsWith('"')) {
    try {
      return JSON.parse(rawText);
    } catch {
      return rawText.slice(1, -1);
    }
  }
  return rawText;
};

const getInitialPageProps = (pathname, search, getPageByPath) => {
  const parsed = parsePathToPageProps(pathname, search);
  if (parsed) return parsed;
  const fallbackPage = getPageByPath(pathname);
  return fallbackPage ? { page: fallbackPage } : {};
};

const AltenarSportsbook = ({ culture, integration }) => {
  const containerRef = useRef(null);
  const sportsBookRef = useRef(null);
  const complianceCheckStartedRef = useRef(false);
  const lastTokenAuthRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const { getPageByPath, goBack, goTo, pushUrl } = useAltenarRoutes();
  const { pathname, search } = useLocation();
  const { lang } = useSettings();
  const { account, authenticated, resolved } = useApplicationState();
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();

  // Bus handlers and the post-init refresh need the latest route values
  // without re-subscribing each render.
  const routesRef = useRef({ getPageByPath, goBack, goTo, pathname, pushUrl, search });
  routesRef.current = { getPageByPath, goBack, goTo, pathname, pushUrl, search };

  const authStateRef = useRef({ authenticated, resolved });
  authStateRef.current = { authenticated, resolved };

  const mountSportsBook = () => {
    if (!window.altenarWSDK || !containerRef.current) return null;
    const initialProps = getInitialPageProps(
      routesRef.current.pathname,
      routesRef.current.search,
      routesRef.current.getPageByPath,
    );

    return window.altenarWSDK.addSportsBook({
      container: containerRef.current,
      props: {
        onBetHistoryButtonClick: event => {
          Logger.info('onBetHistoryButtonClick>>>', event);
        },
        onBetPlacementError: message => {
          Logger.error('onBetPlacementError>>>', message);
          if (!message) return;
          NotificationManager.display({
            data: { rawText: true },
            message,
            status: 'error',
            type: 'notification',
          });
        },
        onGoBack: () => {
          routesRef.current.goBack?.();
        },
        onNavigationButtonClick: ({ page }) => {
          routesRef.current.goTo(page);
        },
        onRouteChangeDetailed: detail => {
          const url = buildPathFromRoute(detail);
          if (!url) return;
          const current = `${routesRef.current.pathname}${routesRef.current.search || ''}`;
          if (url === current) return;
          routesRef.current.pushUrl?.(url);
          // SDK uses memory router, so in-widget navigation never resets the host
          // page scroll. Snap the layout-wrapper back to the top, matching native SB behavior.
          Bus.emit('ui:scroll:content', { behavior: 'auto', scrollTo: 0 });
        },
        onSignInButtonClick: () => {
          signIn();
        },
        routerType: 'memory',
        ...initialProps,
      },
    });
  };

  const refreshAuth = () => {
    if (lastTokenAuthRef.current === true) return;
    lastTokenAuthRef.current = true;
    fetchAltenarToken()
      .then(token => {
        window.altenarWSDK?.set({ token });
      })
      .catch(error => {
        lastTokenAuthRef.current = false;
        Logger.error('Altenar token fetch failed', error);
      });
  };

  useEffect(() => {
    if (!isInitialized) return;
    if (!resolved || !authenticated) return;
    if (lastTokenAuthRef.current === authenticated) return;

    refreshAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, resolved, isInitialized]);

  useEffect(() => {
    const handleLogout = () => {
      window.altenarWSDK?.set({ token: '' });
      lastTokenAuthRef.current = false;
    };

    const handleLogin = () => {
      refreshAuth();
    };

    Bus.on(Bus.events.auth.logout, handleLogout);
    Bus.on(Bus.events.auth.login, handleLogin);
    Bus.on(Bus.events.auth.simpleLogin, handleLogin);

    return () => {
      Bus.off(Bus.events.auth.logout, handleLogout);
      Bus.off(Bus.events.auth.login, handleLogin);
      Bus.off(Bus.events.auth.simpleLogin, handleLogin);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Browser back/forward must drive the widget — popState fires only on
  // user-driven history navigation, never on programmatic history.push.
  useEffect(() => {
    if (!isInitialized) return undefined;

    const handlePopState = () => {
      if (!window.altenarWSDK) return;
      const props = parsePathToPageProps(window.location.pathname, window.location.search);
      if (!props) return;
      window.altenarWSDK.set(props);
    };

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, [isInitialized]);

  useEffect(() => {
    if (!resolved || complianceCheckStartedRef.current) return undefined;
    complianceCheckStartedRef.current = true;

    let cancelled = false;

    const runComplianceChecks = async () => {
      if (!isUserUnderVerification(account)) return true;

      if (isUserNeedKyc(account)) {
        NotificationManager.display({
          message: 'verificationPopup',
          type: 'verificationPopup',
        });
        return false;
      }

      if (isUserNeedBankAccountNumbers(account)) {
        NotificationManager.display({
          message: 'verificationPopup',
          type: 'verificationPopup',
        });
        return false;
      }

      const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
      if (!canProceedSpentLimit) return false;

      const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
      if (!canProceedSelfRegulation) return false;

      return true;
    };

    const initAltenar = async () => {
      const passed = await runComplianceChecks();
      if (cancelled || !passed) return;

      await ensureAltenarLoaded();
      if (cancelled || !window.altenarWSDK) return;

      let token;
      if (authStateRef.current.authenticated) {
        try {
          token = await fetchAltenarToken();
        } catch (error) {
          Logger.error('Altenar token fetch failed', error);
        }
      }
      if (cancelled || !authStateRef.current.authenticated) token = undefined;
      if (cancelled) return;

      window.altenarWSDK.init({
        culture: getCurrentCulture(culture, lang),
        integration,
        ...(token ? { token } : {}),
      });

      sportsBookRef.current = mountSportsBook();

      lastTokenAuthRef.current = Boolean(token);
      setIsInitialized(true);
    };

    initAltenar().then();
    return () => {
      cancelled = true;
      sportsBookRef.current?.remove?.();
      sportsBookRef.current = null;
    };
    // Init must run once after auth is resolved. Auth state changes post-init are
    // handled by refreshAuth and the logout Bus handler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved]);

  useUserSettings();
  return <div className="viewport-height page__main" id={CONTAINER_ID} ref={containerRef} />;
};

AltenarSportsbook.propTypes = {
  culture: PropTypes.string.isRequired,
  integration: PropTypes.string.isRequired,
};

export default AltenarSportsbook;
